import { useCallback } from 'react';
import { MonitorPrompt } from '../../../../shared/models/monitor/MonitorPrompt';
import { PromptsData } from '../../../prompt/models/PromptsData';
import { MonitorPromptsKeywordsStatsResponse } from '../../../../shared/services/monitor/keywords/stats/MonitorPromptsKeywordsStatsResponse';
import { MonitorStatus } from '../../../../shared/types/prompt/MonitorStatusType';

interface UseProjectDataReturn {
  prepareAndSetPromptsData: (
    monitorPromptsData: MonitorPrompt[],
    keywordStats: MonitorPromptsKeywordsStatsResponse[],
    selectedChatType: string,
    allowedChatTypes?: Set<string>
  ) => PromptsData[];
}

export const useProjectData = (): UseProjectDataReturn => {
  const prepareAndSetPromptsData = useCallback(
    (
      monitorPromptsData: MonitorPrompt[],
      keywordStats: MonitorPromptsKeywordsStatsResponse[],
      selectedChatType: string,
      allowedChatTypes?: Set<string>
    ): PromptsData[] => {
      const sortedMonitorPrompts = [...monitorPromptsData].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      );

      const monitorDataByChatType = new Map<
        string,
        Record<string, MonitorPromptsKeywordsStatsResponse>
      >();
      const monitorDataGeneral = new Map<string, MonitorPromptsKeywordsStatsResponse>();
      const monitorDataCombined = new Map<string, { totalPosition: number; count: number }>();

      keywordStats.forEach((data) => {
        const key = `${data.monitorId}_${data.keyword.replace(' ', '_')}`;

        if (data.chatType) {
          if (!monitorDataByChatType.has(key)) {
            monitorDataByChatType.set(key, {});
          }
          monitorDataByChatType.get(key)![data.chatType] = data;

          // Only include models the user has access to in the "ALL" average
          const isAllowed = !allowedChatTypes || allowedChatTypes.has(data.chatType);

          if (isAllowed) {
            if (!monitorDataCombined.has(key)) {
              monitorDataCombined.set(key, {
                totalPosition: 0,
                count: 0,
              });
            }
            if (typeof data.averagePosition === 'number' && data.averagePosition > 0) {
              const combined = monitorDataCombined.get(key)!;
              combined.totalPosition += data.averagePosition;
              combined.count += 1;
            }
          }
        } else {
          monitorDataGeneral.set(key, data);
        }
      });

      const getMonitorData = (prompt: MonitorPrompt, chatType?: string) => {
        const key = `${prompt.id}_${prompt.keyword.replace(' ', '_')}`;
        if (chatType) {
          return monitorDataByChatType.get(key)?.[chatType] ?? null;
        }
        return monitorDataGeneral.get(key) ?? null;
      };

      const getCombinedMonitorData = (prompt: MonitorPrompt) => {
        const key = `${prompt.id}_${prompt.keyword.replace(' ', '_')}`;
        const combinedData = monitorDataCombined.get(key);
        return combinedData && combinedData.count > 0
          ? Math.round((combinedData.totalPosition / combinedData.count) * 10) / 10
          : '-';
      };

      return sortedMonitorPrompts.map((prompt) => {
        const monitorData = getMonitorData(prompt);
        const combinedPosition = getCombinedMonitorData(prompt);
        const searchVolume = prompt.searchVolume;
        const searchVolumeValue = searchVolume > 100 ? String(searchVolume) : '<100';

        const safeValue = (value: any) => (value && value !== 0 ? value : '-');

        return {
          id: prompt.id,
          prompt: prompt.prompt,
          status: prompt.status as MonitorStatus,
          searchKeyword: prompt.searchKeyword,
          searchVolume: searchVolumeValue,
          averagePosition:
            selectedChatType === 'ALL'
              ? combinedPosition && combinedPosition !== '-' && combinedPosition > 0
                ? combinedPosition
                : undefined
              : monitorData?.averagePosition ?? undefined,
          averageGPTPosition: safeValue(getMonitorData(prompt, 'GPT')?.averagePosition),
          averageClaudePosition: safeValue(getMonitorData(prompt, 'CLAUDE')?.averagePosition),
          averageGeminiPosition: safeValue(getMonitorData(prompt, 'GEMINI')?.averagePosition),
          averageDeepSeekPosition: safeValue(getMonitorData(prompt, 'DEEP_SEEK')?.averagePosition),
          averagePerplexityPosition: safeValue(getMonitorData(prompt, 'PERPLEXITY')?.averagePosition),
          averageCopilotPosition: safeValue(getMonitorData(prompt, 'COPILOT_BROWSER')?.averagePosition),
          averageGrokPosition: safeValue(getMonitorData(prompt, 'GROK')?.averagePosition),
          averageAiOverviewPosition: safeValue(
            getMonitorData(prompt, 'AI_OVERVIEW_NEW_BROWSER')?.averagePosition
          ),
          averageAiModePosition: safeValue(getMonitorData(prompt, 'AI_MODE_BROWSER')?.averagePosition),
          shareOfVoice: monitorData?.averageShareOfVoice ? monitorData.averageShareOfVoice + '%' : '-',
          averageGPTShareOfVoice: getMonitorData(prompt, 'GPT')?.averageShareOfVoice ?? '-',
          averageClaudeShareOfVoice: getMonitorData(prompt, 'CLAUDE')?.averageShareOfVoice ?? '-',
          averageGeminiShareOfVoice: getMonitorData(prompt, 'GEMINI')?.averageShareOfVoice ?? '-',
          averageDeepSeekShareOfVoice: getMonitorData(prompt, 'DEEP_SEEK')?.averageShareOfVoice ?? '-',
          averagePerplexityShareOfVoice:
            getMonitorData(prompt, 'PERPLEXITY')?.averageShareOfVoice ?? '-',
          averageCopilotShareOfVoice:
            getMonitorData(prompt, 'COPILOT_BROWSER')?.averageShareOfVoice ?? '-',
          averageGrokShareOfVoice: getMonitorData(prompt, 'GROK')?.averageShareOfVoice ?? '-',
          averageAiOverviewShareOfVoice:
            getMonitorData(prompt, 'AI_OVERVIEW_NEW_BROWSER')?.averageShareOfVoice ?? '-',
          averageAiModeShareOfVoice:
            getMonitorData(prompt, 'AI_MODE_BROWSER')?.averageShareOfVoice ?? '-',
          keyword: prompt.keyword,
          demoVersion: prompt.demoVersion,
          projectId: prompt.projectId,
          created: prompt.created,
          isRequiredAttention: false, // This will be set by the parent component
          hasAiOverview: prompt.hasAiOverview,
          promptGroupId: prompt.promptGroupId ?? null,
        } as PromptsData;
      });
    },
    []
  );

  return {
    prepareAndSetPromptsData,
  };
};
